import { useCallback, useEffect, useState } from 'react'
import type {
  GlossaryDictionaryListItem,
  GlossaryDictionaryUploadResult,
} from '@ai-meeting/shared'
import { ApiError } from '@/services/api'
import {
  deleteGlossaryDictionary,
  downloadGlossaryDictionary,
  downloadGlossaryTemplate,
  downloadSystemGlossaryDictionary,
  fetchGlossaryDictionaries,
  renameGlossaryDictionary,
  reorderGlossaryDictionaries,
  updateGlossaryDictionary,
  uploadGlossaryDictionary,
} from '@/services/glossary'

export function useGlossaryDictionaries() {
  const [system, setSystem] = useState<GlossaryDictionaryListItem[]>([])
  const [user, setUser] = useState<GlossaryDictionaryListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [mutating, setMutating] = useState(false)

  const reload = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await fetchGlossaryDictionaries()
      setSystem(data.system)
      setUser(data.user)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load dictionaries')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const rename = useCallback(async (id: string, name: string) => {
    setMutating(true)
    try {
      const updated = await renameGlossaryDictionary(id, { name })
      setUser((current) => current.map((item) => (item.id === id ? updated : item)))
    } finally {
      setMutating(false)
    }
  }, [])

  const setDefault = useCallback(async (id: string, isDefault: boolean) => {
    let previous: GlossaryDictionaryListItem | undefined
    setUser((current) =>
      current.map((item) => {
        if (item.id !== id) return item
        previous = item
        return { ...item, isDefault }
      }),
    )
    setMutating(true)
    try {
      const updated = await updateGlossaryDictionary(id, { isDefault })
      setUser((current) => current.map((item) => (item.id === id ? updated : item)))
    } catch (err) {
      if (previous) {
        const rollback = previous
        setUser((current) => current.map((item) => (item.id === id ? rollback : item)))
      }
      throw err
    } finally {
      setMutating(false)
    }
  }, [])

  const move = useCallback(
    async (id: string, direction: 'up' | 'down') => {
      const index = user.findIndex((item) => item.id === id)
      if (index < 0) return
      const target = direction === 'up' ? index - 1 : index + 1
      if (target < 0 || target >= user.length) return

      const next = [...user]
      const [removed] = next.splice(index, 1)
      next.splice(target, 0, removed)
      setUser(next)
      setMutating(true)
      try {
        const refreshed = await reorderGlossaryDictionaries({
          orderedIds: next.map((item) => item.id),
        })
        setUser(refreshed)
      } catch (err) {
        await reload()
        throw err
      } finally {
        setMutating(false)
      }
    },
    [reload, user],
  )

  const reorder = useCallback(
    async (fromIndex: number, toIndex: number) => {
      if (
        fromIndex === toIndex ||
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= user.length ||
        toIndex >= user.length
      ) {
        return
      }
      const next = [...user]
      const [removed] = next.splice(fromIndex, 1)
      next.splice(toIndex, 0, removed)
      setUser(next)
      setMutating(true)
      try {
        const refreshed = await reorderGlossaryDictionaries({
          orderedIds: next.map((item) => item.id),
        })
        setUser(refreshed)
      } catch (err) {
        await reload()
        throw err
      } finally {
        setMutating(false)
      }
    },
    [reload, user],
  )

  const remove = useCallback(async (id: string) => {
    setMutating(true)
    try {
      await deleteGlossaryDictionary(id)
      setUser((current) => current.filter((item) => item.id !== id))
    } finally {
      setMutating(false)
    }
  }, [])

  const uploadOne = useCallback(
    async (file: File): Promise<GlossaryDictionaryUploadResult> => {
      setMutating(true)
      try {
        const result = await uploadGlossaryDictionary(file)
        setUser((current) => [...current, result])
        return result
      } finally {
        setMutating(false)
      }
    },
    [],
  )

  return {
    system,
    user,
    loading,
    error,
    mutating,
    reload,
    rename,
    setDefault,
    move,
    reorder,
    remove,
    uploadOne,
    downloadUser: downloadGlossaryDictionary,
    downloadSystem: downloadSystemGlossaryDictionary,
    downloadTemplate: downloadGlossaryTemplate,
  }
}
